import { useMemo, useState } from "react";
import { api } from "../api/client";
import type { StaffOrder } from "../api/types";
import { useAppState } from "../state/appState";
import { useSession } from "../state/session";
import { useToasts } from "../state/toasts";
import { Empty, Modal, StatusChip, useTicker } from "../ui/components";
import { elapsed } from "../ui/format";
import { TopBar } from "../ui/TopBar";
import { MoneyLine, OrderLines, OrderNotes, PhoneLink, TicketHead } from "./OrderTicket";

type Tab = "available" | "mine" | "done";

const TABS: { id: Tab; title: string }[] = [
  { id: "available", title: "Доступные" },
  { id: "mine", title: "Мои" },
  { id: "done", title: "Завершённые" }
];

export function CourierScreen() {
  const { snapshot } = useAppState();
  const { staff } = useSession();
  const { run } = useToasts();
  const now = useTicker();

  const [tab, setTab] = useState<Tab>("available");
  const [releaseFor, setReleaseFor] = useState<StaffOrder | null>(null);
  const [reason, setReason] = useState("");

  const orders = snapshot?.orders ?? [];
  const myId = staff?.id;

  const visible = useMemo(() => {
    if (tab === "available") {
      return orders
        .filter(order => order.status === "ready" && !order.courierId)
        .sort((a, b) => new Date(a.paidAt ?? a.createdAt).getTime() - new Date(b.paidAt ?? b.createdAt).getTime());
    }
    if (tab === "mine") {
      return orders.filter(order => order.courierId === myId && order.status === "picked_up");
    }
    return orders.filter(order => order.courierId === myId && order.status === "delivered");
  }, [orders, tab, myId]);

  /*
   * Доступные заказы собираются по лавочкам: на одной лавочке может сидеть
   * компания, где каждый заказал сам за себя, а доставщик идёт туда один раз.
   * Порядок групп — по самому раннему заказу: кто ждёт дольше, к тому и идти.
   */
  const groups = useMemo(() => {
    if (tab !== "available") return [];

    const byBench = new Map<string, StaffOrder[]>();
    for (const order of visible) {
      const key = order.benchId ?? "—";
      const group = byBench.get(key);
      if (group) group.push(order);
      else byBench.set(key, [order]);
    }

    return [...byBench.entries()].map(([bench, items]) => ({ bench, items }));
  }, [visible, tab]);

  const availableCount = orders.filter(order => order.status === "ready" && !order.courierId).length;
  const mineCount = orders.filter(order => order.courierId === myId && order.status === "picked_up").length;

  async function claim(order: StaffOrder) {
    const ok = await run(
      () => api.once.post(`/api/orders/${order.id}/claim`),
      `Заказ №${order.number} закреплён за вами`
    );
    if (ok) setTab("mine");
  }

  async function claimBench(bench: string, count: number) {
    const ok = await run(
      () => api.once.post(`/api/benches/${bench}/claim`),
      count > 1 ? `Лавочка №${bench}: ${count} заказа закреплены за вами` : "Заказ закреплён за вами"
    );
    if (ok) setTab("mine");
  }

  async function deliver(order: StaffOrder) {
    await run(
      () => api.once.patch(`/api/orders/${order.id}/status`, { status: "delivered" }),
      `Заказ №${order.number} доставлен`
    );
  }

  async function release() {
    if (!releaseFor) return;
    const ok = await run(
      () => api.post(`/api/orders/${releaseFor.id}/release`, { reason: reason.trim() || undefined }),
      "Заказ вернулся в общий список"
    );
    if (ok) {
      setReleaseFor(null);
      setReason("");
    }
  }

  return (
    <div className="shell">
      <TopBar title="Доставка" subtitle="панель доставщика" />

      <main className="page">
        <div className="page-head">
          <div>
            <p className="eyebrow">Смена</p>
            <h1>Заказы к лавочкам</h1>
          </div>
        </div>

        <div className="tabs">
          {TABS.map(item => (
            <button
              type="button"
              key={item.id}
              className={`tab ${tab === item.id ? "active" : ""}`}
              onClick={() => setTab(item.id)}
            >
              {item.title}
              {item.id === "available" && availableCount ? ` · ${availableCount}` : ""}
              {item.id === "mine" && mineCount ? ` · ${mineCount}` : ""}
            </button>
          ))}
        </div>

        <div className="stack">
          {visible.length === 0 ? (
            <Empty>
              {tab === "available"
                ? "Готовых заказов пока нет. Они появятся здесь сами."
                : tab === "mine"
                  ? "У вас нет заказов в доставке."
                  : "Завершённых заказов пока нет."}
            </Empty>
          ) : null}

          {tab === "available"
            ? groups.map(group => (
                <section className={`deliver-group ${group.items.length > 1 ? "many" : ""}`} key={group.bench}>
                  {group.items.length > 1 ? (
                    <header className="deliver-group-head">
                      <h2>Лавочка №{group.bench}</h2>
                      <span className="tiny muted">{group.items.length} заказа · разные гости</span>
                    </header>
                  ) : null}

                  {group.items.map(order => (
                    <article className="deliver-card" key={order.id}>
                      <TicketHead order={order} now={now} />
                      <StatusChip status={order.status} />
                      <OrderLines order={order} />
                      <OrderNotes order={order} />
                      <MoneyLine order={order} />
                    </article>
                  ))}

                  {/* Забирается вся лавочка разом: доставщик идёт туда один раз,
                      и три нажатия вместо одного — это три щели, в которые
                      второй доставщик уносит половину компании. */}
                  <button
                    type="button"
                    className="btn btn-primary btn-xl btn-block"
                    onClick={() =>
                      group.items.length > 1
                        ? void claimBench(group.bench, group.items.length)
                        : void claim(group.items[0]!)
                    }
                  >
                    {group.items.length > 1 ? `Забрать все ${group.items.length}` : "Забрать заказ"}
                  </button>
                </section>
              ))
            : null}

          {tab === "available" ? null : visible.map(order => (
            <article className="deliver-card" key={order.id}>
              <TicketHead order={order} now={now} />
              <StatusChip status={order.status} />
              <OrderLines order={order} />
              <OrderNotes order={order} />
              <MoneyLine order={order} />

              <PhoneLink order={order} />

              {tab === "done" ? (
                <p className="tiny muted">Доставлен · {elapsed(order.paidAt, new Date(order.updatedAt).getTime())} от оплаты</p>
              ) : null}

              {tab === "mine" ? (
                <>
                  <button type="button" className="btn btn-primary btn-xl btn-block" onClick={() => void deliver(order)}>
                    Доставлен
                  </button>
                  <button type="button" className="btn btn-block" onClick={() => setReleaseFor(order)}>
                    Отказаться от заказа
                  </button>
                </>
              ) : null}
            </article>
          ))}
        </div>
      </main>

      {releaseFor ? (
        <Modal
          title={`Отказ от заказа №${releaseFor.number}`}
          onClose={() => setReleaseFor(null)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setReleaseFor(null)}>
                Отмена
              </button>
              <button type="button" className="btn btn-danger" onClick={() => void release()}>
                Вернуть в общий список
              </button>
            </>
          }
        >
          <p>Заказ снова станет доступен всем доставщикам, а причина сохранится в заметках.</p>
          <label className="field">
            <span>Причина</span>
            <input
              autoFocus
              value={reason}
              onChange={event => setReason(event.target.value)}
              placeholder="Не нашёл лавочку"
            />
          </label>
        </Modal>
      ) : null}
    </div>
  );
}
